import { useState, type ReactNode } from 'react';
import { formatTime, formatTimecode } from '../format/helpers';

// Timecode display mode (frame timecode vs plain seconds), shared across all
// transports via localStorage; each instance reads it on mount.
const TIMECODE_MODE_KEY = 'fabled.timecodeMode';

function readSecondsMode(): boolean {
  try {
    return localStorage.getItem(TIMECODE_MODE_KEY) === 'seconds';
  } catch {
    return false;
  }
}

interface TransportProps {
  playing: boolean;
  currentTime: number;
  duration: number;
  /** When set, timecode is frame-accurate HH:MM:SS:FF instead of mm:ss.d. */
  fps?: number;
  onToggle: () => void;
  onSeek: (t: number) => void;
  /** When provided, renders go-to-start / step / go-to-end buttons. */
  onStep?: (dir: 1 | -1) => void;
  /** When true, renders a scrub slider between play and timecode. */
  scrubber?: boolean;
  className?: string;
  playClassName?: string;
  timecodeClassName?: string;
  /** Trailing extra controls (e.g. fullscreen). */
  children?: ReactNode;
}

export default function Transport({
  playing,
  currentTime,
  duration,
  fps,
  onToggle,
  onSeek,
  onStep,
  scrubber,
  className,
  playClassName,
  timecodeClassName,
  children,
}: TransportProps) {
  const [secondsMode, setSecondsMode] = useState(readSecondsMode);
  const fmt = (t: number) =>
    fps && !secondsMode ? formatTimecode(t, fps) : formatTime(t, true);
  const toggleTimecodeMode = () => {
    const next = !secondsMode;
    setSecondsMode(next);
    try {
      localStorage.setItem(TIMECODE_MODE_KEY, next ? 'seconds' : 'timecode');
    } catch {
      /* ignore */
    }
  };
  return (
    <div className={className}>
      {onStep && (
        <>
          <button className="icon" title="Go to start (Home)" onClick={() => onSeek(0)}>
            ⏮
          </button>
          <button className="icon" title="Step back (←)" onClick={() => onStep(-1)}>
            ◂▮
          </button>
        </>
      )}
      <button
        className={`icon ${playClassName ?? ''}`}
        title="Play/Pause (Space)"
        onClick={onToggle}
      >
        {playing ? '❚❚' : '▶'}
      </button>
      {onStep && (
        <>
          <button className="icon" title="Step forward (→)" onClick={() => onStep(1)}>
            ▮▸
          </button>
          <button className="icon" title="Go to end (End)" onClick={() => onSeek(duration)}>
            ⏭
          </button>
        </>
      )}
      {scrubber && (
        <input
          className="viewer-scrub"
          type="range"
          min={0}
          max={Math.max(0.001, duration)}
          step={0.01}
          value={Math.min(currentTime, duration)}
          onChange={(e) => onSeek(parseFloat(e.target.value))}
        />
      )}
      <span
        className={`timecode ${timecodeClassName ?? ''}`}
        title={fps ? 'Toggle timecode / seconds' : undefined}
        style={fps ? { cursor: 'pointer' } : undefined}
        onClick={fps ? toggleTimecodeMode : undefined}
      >
        {fmt(currentTime)} <span className="preview-timecode-sep">/</span> {fmt(duration)}
      </span>
      {children}
    </div>
  );
}
